import { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { CapabilityDetailLayout, WORKING_COPY_ID } from '@/components/capability-detail/CapabilityDetailLayout';
import { useEditorBundle, useBundleRoot } from '@/hooks/use-editor-bundle';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Users, Lock } from 'lucide-react';
import { api } from '@/lib/api-client';

interface RoleMetadata {
    permissions: string[];
    lifecycle?: string;
    delegation_authority?: string;
}

export default function RoleDetailPage() {
    const { appKey, name } = useParams();
    const [selectedRevisionId, setSelectedRevisionId] = useState(WORKING_COPY_ID);
    const { data: bundle, isLoading } = useEditorBundle(appKey!, name!);
    const data = useBundleRoot(bundle) as Record<string, unknown> | null;
    const [assignmentCount, setAssignmentCount] = useState<number | null>(null);

    const metadata = extractRoleMetadata(data);

    useEffect(() => {
        if (!name) return;
        api.get(`/org/members?role=${encodeURIComponent(name)}`)
            .then(res => setAssignmentCount(res.data?.total ?? res.data?.length ?? null))
            .catch(() => setAssignmentCount(null));
    }, [name]);

    if (isLoading) return <div className="p-6 text-muted-foreground">Loading role...</div>;

    const permissionsByNamespace = groupByNamespace(metadata.permissions);

    return (
        <CapabilityDetailLayout
            typeLabel="Role"
            appKey={appKey}
            capabilityName={name}
            entityData={data as Record<string, unknown> | undefined}
            selectedRevisionId={selectedRevisionId}
            onRevisionChange={setSelectedRevisionId}
        >
            <div className="p-6 space-y-6">
                <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100">
                        <Shield className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold">{name}</h2>
                        <p className="text-sm text-muted-foreground">
                            {(data as any)?.description ?? 'No description'}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                    <Card>
                        <CardContent className="pt-4">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Permissions</p>
                            <p className="text-2xl font-semibold mt-1">{metadata.permissions.length}</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Lifecycle</p>
                            <Badge className="mt-1" variant={metadata.lifecycle === 'active' ? 'default' : 'secondary'}>
                                {metadata.lifecycle ?? 'active'}
                            </Badge>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Assigned Users</p>
                            <div className="flex items-center gap-2 mt-1">
                                <Users className="h-4 w-4 text-muted-foreground" />
                                <span className="text-2xl font-semibold">{assignmentCount ?? '—'}</span>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {metadata.delegation_authority && (
                    <div className="flex items-center gap-2 rounded-lg border p-3 bg-muted/30">
                        <Lock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                            Delegation authority: <strong>{metadata.delegation_authority}</strong>
                        </span>
                    </div>
                )}

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Permissions ({metadata.permissions.length})</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {Object.entries(permissionsByNamespace).map(([ns, perms]) => (
                            <div key={ns}>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                    {ns} ({perms.length})
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {perms.map(p => (
                                        <Badge key={p} variant="outline" className="font-mono text-xs">
                                            {p}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        ))}
                        {metadata.permissions.length === 0 && (
                            <p className="text-sm text-muted-foreground">No permissions defined for this role.</p>
                        )}
                    </CardContent>
                </Card>
            </div>
        </CapabilityDetailLayout>
    );
}

function extractRoleMetadata(data: Record<string, unknown> | null): RoleMetadata {
    if (!data) return { permissions: [] };

    const revisions = (data.immutable_revisions as any[]) ?? [];
    const working = data.working_revision as Record<string, unknown> | null;

    const candidates = [...revisions, working].filter(Boolean);
    for (const rev of candidates) {
        // Prefer type_config (import path); fall back to metadata (legacy SQL seed).
        const sources = [(rev as any)?.type_config, (rev as any)?.metadata];
        for (const src of sources) {
            if (src?.permissions && Array.isArray(src.permissions)) {
                return {
                    permissions: src.permissions,
                    lifecycle: src.lifecycle,
                    delegation_authority: src.delegation_authority,
                };
            }
        }
    }

    return { permissions: [] };
}

function groupByNamespace(permissions: string[]): Record<string, string[]> {
    const groups: Record<string, string[]> = {};
    for (const p of permissions) {
        const ns = p.split(':')[0] ?? 'other';
        if (!groups[ns]) groups[ns] = [];
        groups[ns].push(p);
    }
    return groups;
}
